import React, { useState, useEffect } from 'react';
import { db, auth } from '../firebase';
import { getAuth } from 'firebase/auth';
import {
  getFirestore,
  collection,
  addDoc,
  serverTimestamp,
  query,
  where,
  getDocs,
  doc,
  getDoc
} from 'firebase/firestore';
import {
  getStorage,
  ref,
  uploadBytesResumable,
  getDownloadURL
} from 'firebase/storage';
import { useNavigate, useLocation } from 'react-router-dom';
import BackHomeButton from '../components/BackHomeButton';

// ✅ Added Lucide icons for a modern UI
import { 
  UploadCloud, BookOpen, Layers, Tag, Type, 
  FileText, Info, AlertCircle, CheckCircle2 
} from 'lucide-react';

function UploadFilePage() {
  const [file, setFile] = useState(null);
  const [description, setDescription] = useState('');
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState(''); // 'error' or 'success'
  const [subject, setSubject] = useState('');
  const [topicName, setTopicName] = useState('');
  const [type, setType] = useState('Worksheet');
  const navigate = useNavigate();

  const [usr, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);

  const [savedLessons, setSavedLessons] = useState([]);
  const [selectedLessonId, setSelectedLessonId] = useState('');
  const [selectedLessonName, setSelectedLessonName] = useState('');

  const [text, setText] = useState('');
  const [lessonName, setLessonName] = useState('');

  const user = getAuth().currentUser;
  const isAdmin = user?.email === 'eascode.ltd@gmail.com';
  const location = useLocation();

  useEffect(() => {
    const state = location.state;
    if (state) {
      if (state.selectedLessonId) setSelectedLessonId(state.selectedLessonId);
      if (state.subject) setSubject(state.subject);
    }
  }, [location.state]);

  useEffect(() => {
    const fetchSavedLessons = async () => {
      const auth = getAuth();
      const user = auth.currentUser;
      if (!user) {
        alert("You must be logged in");
        navigate('/login');
      } else {
        setUser(user);
        const docRef = doc(db, 'profiles', user.uid);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const profile = docSnap.data();
          setUserProfile(profile);

          const snapshot = await getDocs(collection(db, 'Saved_lessons'));
          const lessons = snapshot.docs
            .map(doc => ({ id: doc.id, ...doc.data() }))
            .filter(item =>
              (item.email === user.email || item.sharedWith === 'parents') &&
              item.standard === profile.standard && item.school === profile.school
            );
          setSavedLessons(lessons);
        }
      }
    };

    fetchSavedLessons();
  }, [navigate]);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      const maxSizeInBytes = 10 * 1024 * 1024;
      if (selectedFile.size === 0) {
        setMessage('File is empty. Please select a valid worksheet file.');
        setMessageType('error');
        setFile(null);
        return;
      }

      if (selectedFile.size > maxSizeInBytes) {
        setMessage('File size exceeds 10 MB. Please select a smaller file.');
        setMessageType('error');
        setFile(null);
        return;
      }
      setMessage(''); 
      setMessageType('');
      setFile(selectedFile);
    }
  };

  const handleUpload = async () => {
    const trimmedDescription = description.trim();
    const trimmedSubject = subject.trim();
    const trimmedTopicName = topicName.trim();

    if (!file || file.size === 0 || !trimmedDescription || !trimmedSubject || !trimmedTopicName || !selectedLessonId) {
      setMessage('Please fill in all required fields and select a file.');
      setMessageType('error');
      return;
    }

    const selectedLesson = savedLessons.find(lesson => lesson.id === selectedLessonId);
    const finalLessonName = selectedLesson?.lessonName || '';

    const auth = getAuth();
    const user = auth.currentUser;
    if (!user) {
      setMessage('User not logged in');
      setMessageType('error');
      return;
    }

    //const db = getFirestore();
    const profilesRef = collection(db, 'profiles');
    const querySnapshot = await getDocs(query(profilesRef, where('uid', '==', user.uid)));

    if (querySnapshot.empty) {
      setMessage('Profile not found for this user.');
      setMessageType('error');
      return;
    }

    const profileData = querySnapshot.docs[0].data();
    const storage = getStorage();

    const folder = type === 'Test'
      ? `${profileData.standard}_${trimmedSubject}_Test_By_Admin_Review`
      : 'uploads_Review';

    const storageRef = ref(storage, `${folder}/${file.name}`);
    const uploadTask = uploadBytesResumable(storageRef, file);

    setUploading(true);
    setMessage('');

    uploadTask.on('state_changed',
      (snapshot) => {
        const prog = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
        setProgress(prog);
      },
      (error) => {
        console.error(error);
        setUploading(false);
        setMessage('Error uploading file. Please try again.');
        setMessageType('error');
      },
      async () => {
        const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
        const calculatedFileName = `Std_${profileData.standard}_${trimmedTopicName}`;

        const uploadDocRef = await addDoc(collection(db, 'uploads_Review'), {
          fileName: file.name,
          fileUrl: downloadURL,
          description: trimmedDescription,
          subject: trimmedSubject,
          topicName: trimmedTopicName,
          calculatedFileName,
          uploadedBy: user.email,
          uid: user.uid,
          uploadTime: serverTimestamp(),
          standard: profileData.standard || '',
          school: profileData.school || '',
          city: profileData.city || '',
          type,
          selectedLessonId,
          lessonName: finalLessonName,
          reviewStatus: 'pending',
          reviewRequestedAt: serverTimestamp()
        });

        setUploading(false);
        setFile(null);
        setDescription('');
        setSubject('');
        setTopicName('');
        setType('Worksheet');
        setProgress(0);
        navigate('/SubmissionThankYou', {
          replace: true,
          state: {
            submissionType: type === 'Worksheet' ? 'worksheet' : 'upload',
            submissionDetails: {
              reviewUploadId: uploadDocRef.id,
              reviewCollection: 'uploads_Review',
              fileName: file.name,
              fileUrl: downloadURL,
              subject: trimmedSubject,
              topicName: trimmedTopicName,
              documentType: type,
              lessonName: finalLessonName,
              selectedLessonId
            },
            returnState: { selectedLessonId, subject: trimmedSubject }
          }
        });
      }
    );
  };

  return (
    <div className="min-h-screen bg-[#F4F6FA] py-6 px-4 sm:px-6 pb-24 font-sans">
      <div className="max-w-xl mx-auto">
        
        {/* Back Button */}
        <div className="mb-4">
          <BackHomeButton backState={{ selectedLessonId, subject }} />
        </div>

        {/* Header Section */}
        <div className="text-center mb-6">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
            Upload Worksheet
          </h2>
          <p className="text-sm text-gray-500">
            Share your resources with the community. <br className="md:hidden" />
            <span className="font-semibold text-blue-600">For Parents, By Parents and Teachers</span>
          </p>
        </div>

        {/* Info Banner */}
        <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 flex items-start gap-3 mb-6 shadow-sm">
          <Info className="w-5 h-5 text-blue-500 shrink-0 mt-0.5" />
          <div className="text-xs md:text-sm text-blue-800">
            <p className="font-semibold mb-1">Thank you for contributing!</p>
            <p className="opacity-90">Some subjects require a lesson to be created before a worksheet can be submitted. Ensure your file is under 5MB.</p>
          </div>
        </div>

        {/* Error / Success Messages */}
        {message && (
          <div className={`mb-6 p-4 rounded-xl flex items-center gap-3 text-sm font-semibold shadow-sm ${
            messageType === 'error' ? 'bg-red-50 text-red-700 border border-red-200' : 'bg-green-50 text-green-700 border border-green-200'
          }`}>
            {messageType === 'error' ? <AlertCircle className="w-5 h-5" /> : <CheckCircle2 className="w-5 h-5" />}
            {message}
          </div>
        )}

        {/* MAIN FORM CARD */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 md:p-8 space-y-5">

          {/* 1. File Upload */}
          <div>
            <label className="block text-xs md:text-sm font-bold text-gray-700 mb-2 flex items-center gap-1.5">
              <UploadCloud className="w-4 h-4 text-blue-500" /> Choose File
            </label>
            <input 
              type="file" 
              onChange={handleFileChange} 
              className="w-full text-sm text-gray-500 file:mr-4 file:py-2.5 file:px-4 file:rounded-xl file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 transition-all border border-gray-200 rounded-xl cursor-pointer bg-gray-50 focus:outline-none"
            />
          </div>

          {/* 2. Subject Dropdown */}
          <div>
            <label className="block text-xs md:text-sm font-bold text-gray-700 mb-2 flex items-center gap-1.5">
              <BookOpen className="w-4 h-4 text-blue-500" /> Choose Subject
            </label>
            <select
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm md:text-base text-gray-800 focus:bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all cursor-pointer"
            >
              <option value="">Select Subject</option>
              {['Biology', 'Chemistry', 'Computer', 'English Language', 'English Literature', 'EVS', 'French', 'GK', 'Geography', 'Hindi Language', 'Hindi Literature', 'History Civics', 'Maths', 'Moral Science', 'Physics', 'Social Studies', 'Science', 'Other'].map(sub => (
                <option key={sub} value={sub}>{sub}</option>
              ))}
            </select>
          </div>

          {/* 3. Lesson Dropdown (Conditional) */}
          {subject && (
            <div className="animate-fadeIn">
              <label className="block text-xs md:text-sm font-bold text-gray-700 mb-2 flex items-center gap-1.5">
                <Layers className="w-4 h-4 text-blue-500" /> Lessons under {subject}
              </label>
              <select
                value={selectedLessonId}
                onChange={(e) => {
                  const selected = savedLessons.find(l => l.id === e.target.value);
                  setSelectedLessonId(e.target.value);
                  if (selected) {
                    setText(selected.text || '');
                    setLessonName(selected.lessonName || '');
                  }
                }}
                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm md:text-base text-gray-800 focus:bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all cursor-pointer"
              >
                <option value="">-- Select a Lesson --</option>
                {savedLessons
                  .filter((lesson) => lesson.subject === subject)
                  .map((lesson) => (
                    <option key={lesson.id} value={lesson.id}>
                      Lesson Name - {lesson.lessonName || 'No Title'}
                    </option>
                  ))}
                <option value="Use without a Lesson">Use without a lesson</option>
              </select>
            </div>
          )}

          {/* 4. Topic Name */}
          <div>
            <label className="block text-xs md:text-sm font-bold text-gray-700 mb-2 flex items-center gap-1.5">
              <Tag className="w-4 h-4 text-blue-500" /> Topic Name
            </label>
            <input
              type="text"
              value={topicName}
              onChange={(e) => setTopicName(e.target.value)}
              placeholder="E.g., Addition & Subtraction"
              className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm md:text-base text-gray-800 focus:bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all placeholder-gray-400"
            />
          </div>

          {/* 5. Type Dropdown */}
          <div>
            <label className="block text-xs md:text-sm font-bold text-gray-700 mb-2 flex items-center gap-1.5">
              <Type className="w-4 h-4 text-blue-500" /> Document Type
            </label>
            <select
              value={type}
              onChange={(e) => setType(e.target.value)}
              className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm md:text-base text-gray-800 focus:bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all cursor-pointer"
            >
              <option value="Worksheet">Parent Worksheets</option>
              {isAdmin && <option value="Test">Admin Tests</option>}
              <option value="Assessment">School Assessment</option>
              <option value="Spelling_Practice">Spelling Practice</option>
            </select>
          </div>

          {/* 6. Description */}
          <div>
            <label className="block text-xs md:text-sm font-bold text-gray-700 mb-2 flex items-center justify-between">
              <span className="flex items-center gap-1.5">
                <FileText className="w-4 h-4 text-blue-500" /> Description
              </span>
              <span className="text-[10px] md:text-xs text-gray-400 font-normal">
                {description.length}/50
              </span>
            </label>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={50}
              placeholder="Short description of the file..."
              className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm md:text-base text-gray-800 focus:bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all placeholder-gray-400"
            />
          </div>

          {/* Submit Button */}
          <div className="pt-2">
            <button
              onClick={handleUpload}
              disabled={uploading}
              className={`w-full py-3.5 md:py-4 rounded-xl text-white font-bold text-sm md:text-base shadow-md transition-all duration-200 flex justify-center items-center gap-2 ${
                uploading 
                  ? 'bg-blue-400 cursor-not-allowed' 
                  : 'bg-blue-600 hover:bg-blue-700 hover:shadow-lg active:scale-[0.98]'
              }`}
            >
              {uploading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Uploading {progress}%
                </>
              ) : (
                'Upload Worksheet'
              )}
            </button>
          </div>

          {/* Progress Bar */}
          {uploading && (
            <div className="w-full bg-gray-100 rounded-full h-2.5 mt-4 overflow-hidden shadow-inner">
              <div 
                className="bg-blue-500 h-2.5 rounded-full transition-all duration-300 ease-out" 
                style={{ width: `${progress}%` }}
              ></div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}

export default UploadFilePage;
